import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchReturnOrders, updateOrderReturnStatus } from 'store/features/orders/orderAction';
import {
  selectReturnOrders,
  selectReturnLoading,
  selectReturnUpdating,
  selectVendorError,
} from 'store/features/orders/orderSelectors';
import { FiPackage, FiCalendar, FiRefreshCw, FiCheck, FiX, FiClipboard } from 'react-icons/fi';
import { format } from 'date-fns';

const ReturnOrders = () => {
  const dispatch = useDispatch();
  const returnOrders = useSelector(selectReturnOrders);
  const loading = useSelector(selectReturnLoading);
  const updating = useSelector(selectReturnUpdating);
  const error = useSelector(selectVendorError);
  const [editingOrderId, setEditingOrderId] = useState(null);

  useEffect(() => {
    dispatch(fetchReturnOrders());
  }, [dispatch]);

  const handleReturnAction = async (orderId, action) => {
    try {
      await dispatch(updateOrderReturnStatus(orderId, action));
      setEditingOrderId(null);
      dispatch(fetchReturnOrders());
    } catch (error) {
      console.error('Failed to update return status:', error);
    }
  };

  const getReturnStatusBadge = (status) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    
    switch (status) {
      case 'Requested':
        return (
          <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
            <FiRefreshCw className="mr-1" /> Requested
          </span>
        );
      case 'Approved':
        return (
          <span className={`${baseClasses} bg-blue-100 text-blue-800`}>
            <FiCheck className="mr-1" /> Approved
          </span>
        );
      case 'Rejected':
        return (
          <span className={`${baseClasses} bg-red-100 text-red-800`}>
            <FiX className="mr-1" /> Rejected
          </span>
        );
      case 'Completed':
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            <FiCheck className="mr-1" /> Completed
          </span>
        );
      default:
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            {status}
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-600 bg-red-100 rounded-lg">
        Error loading return orders: {error}
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 h-screen">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-gray-800">Return Orders</h1>
        <div className="flex items-center space-x-4">
          <div className="bg-white px-4 py-2 rounded-lg shadow-sm">
            <span className="text-gray-700">Total Return Requests:</span>
            <span className="ml-2 font-semibold">{returnOrders.length}</span>
          </div>
        <div></div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-indigo-600 text-white">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Order ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Customer Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Products
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Order Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Return Requested
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Return Reason
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Return Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {returnOrders.map((order) => (
                <tr key={order.OrderId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    #{order.OrderId?.slice(-6).toUpperCase()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {order.CustomerEmail}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center">
                        <FiPackage className="h-5 w-5 text-indigo-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {order.ProductsName?.length || 0} item{(order.ProductsName?.length || 0) !== 1 ? 's' : ''}
                        </div>
                        <div className="text-sm text-gray-500">
                          {order.ProductsName?.[0] || 'N/A'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <FiCalendar className="mr-2 text-gray-400" />
                      {order.Date ? format(new Date(order.Date), 'MMM dd, yyyy') : 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <FiCalendar className="mr-2 text-gray-400" />
                      {order.returnRequestedAt ? format(new Date(order.returnRequestedAt), 'MMM dd, yyyy HH:mm') : 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      ₹{order.Amount?.toFixed(2) || '0.00'}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <div className="flex items-center">
                      <FiClipboard className="mr-2 text-gray-400" />
                      {order.returnReason || 'No reason provided'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getReturnStatusBadge(order.returnStatus)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {order.returnStatus === 'Requested' && (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleReturnAction(order.OrderId, 'approve')}
                          disabled={updating}
                          className="text-green-600 hover:text-green-900 disabled:opacity-50"
                        >
                          {updating ? 'Approving...' : 'Approve'}
                        </button>
                        <button
                          onClick={() => handleReturnAction(order.OrderId, 'reject')}
                          disabled={updating}
                          className="text-red-600 hover:text-red-900 disabled:opacity-50"
                        >
                          {updating ? 'Rejecting...' : 'Reject'}
                        </button>
                      </div>
                    )}
                    {order.returnStatus === 'Approved' && (
                      <button
                        onClick={() => handleReturnAction(order.OrderId, 'complete')}
                        disabled={updating}
                        className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                      >
                        {updating ? 'Completing...' : 'Complete'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {returnOrders.length === 0 && !loading && (
        <div className="text-center py-12">
          <FiRefreshCw className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No return requests found</h3>
          <p className="mt-1 text-sm text-gray-500">There are no return requests at the moment.</p>
        </div>
      )}
    </div>
  );
};

export default ReturnOrders;